import axios, { AxiosError } from "axios";
import { useAuthStore } from "@/modules/auth/stores/auth";
import { API_URL } from "@/shared/utils/env";
import { UnexpectedError } from "@/shared/utils/custom-errors";
import qs from "qs";

type UnexpectedApiError = UnexpectedError & {
  data?: unknown;
  response?: AxiosError["response"];
  status?: number;
};

type ApiErrorItem = Record<string, unknown>;

type ApiErrorPayload = {
  detail?: string;
  error?: string;
  items?: ApiErrorItem[];
  message?: string;
  non_field_errors?: string | string[];
} & Record<string, unknown>;

const getAuthHeaders = () => {
  // return auth header with jwt if user is logged in and request is to the api url
  const { token } = useAuthStore();
  if (token != null) {
    return { Authorization: `Bearer ${token?.access}` };
  } else {
    return {};
  }
};

const instance = axios.create({
  baseURL: API_URL,
  paramsSerializer: {
    serialize: function (params: Record<string, unknown>) {
      return qs.stringify(params, { arrayFormat: "repeat" });
    },
  },
});

const buildUnexpectedError = (
  message: string,
  error?: AxiosError
): UnexpectedApiError => {
  const unexpectedError = new UnexpectedError(message) as UnexpectedApiError;

  if (error?.response) {
    unexpectedError.response = error.response;
    unexpectedError.status = error.response.status;
    unexpectedError.data = error.response.data;
  }

  return unexpectedError;
};

// Positional keys ("0", "1", ...) come from list-shaped errors serialized as
// objects — they aren't field names, so they never prefix their message.
const isPositionalKey = (key: string): boolean => /^\d+$/.test(key);

// Keys whose messages read as full sentences already; everything else gets a
// "<field>: " prefix.
const isUnprefixedKey = (key: string): boolean =>
  key === "error" ||
  key === "detail" ||
  key === "message" ||
  key === "non_field_errors" ||
  isPositionalKey(key);

const normalizeErrorValue = (value: unknown): string | null => {
  if (Array.isArray(value)) {
    const messages = value
      .map((item) => normalizeErrorValue(item))
      .filter((item): item is string => Boolean(item));

    return messages.length > 0 ? messages.join(", ") : null;
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  if (value && typeof value === "object") {
    const messages = Object.entries(value as Record<string, unknown>)
      .map(([key, nestedValue]) => {
        const nestedMessage = normalizeErrorValue(nestedValue);

        if (!nestedMessage) {
          return null;
        }

        return key && !isPositionalKey(key)
          ? `${key}: ${nestedMessage}`
          : nestedMessage;
      })
      .filter((item): item is string => Boolean(item));

    return messages.length > 0 ? messages.join(", ") : null;
  }

  return null;
};

const extractCombinedErrorMessage = (responseData: unknown): string | null => {
  if (typeof responseData === "string") {
    return responseData;
  }

  // A top-level array body is a plain message list (e.g. a DRF view-level
  // ValidationError) — join the messages without inventing "0:" prefixes.
  if (Array.isArray(responseData)) {
    return normalizeErrorValue(responseData);
  }

  if (!responseData || typeof responseData !== "object") {
    return null;
  }

  const errorMessages: string[] = [];
  const normalizedResponse = responseData as ApiErrorPayload;

  if (Array.isArray(normalizedResponse.items)) {
    normalizedResponse.items.forEach((item) => {
      const keys = Object.keys(item || {});
      keys.forEach((key) => {
        const rawMessage = normalizeErrorValue(item[key]);

        if (!rawMessage) {
          return;
        }

        if (isUnprefixedKey(key)) {
          errorMessages.push(String(rawMessage));
        } else {
          errorMessages.push(`${key}: ${rawMessage}`);
        }
      });
    });
  }

  const entries = Object.entries(normalizedResponse);
  if (entries.length === 0) {
    return errorMessages.length > 0 ? errorMessages.join("; ") : null;
  }

  entries.forEach(([key, value]) => {
    if (key === "items") {
      return;
    }

    const rawMessage = normalizeErrorValue(value);

    if (!rawMessage) {
      return;
    }

    if (key && !isUnprefixedKey(key)) {
      errorMessages.push(`${key}: ${rawMessage}`);
    } else {
      errorMessages.push(String(rawMessage));
    }
  });

  return errorMessages.length > 0 ? errorMessages.join("; ") : null;
};

instance.interceptors.request.use((config) => {
  // if url starts with http it means that's external url
  if (!(config.url ?? "").startsWith("http")) {
    config.headers = Object.assign(config.headers, getAuthHeaders());
  }
  return config;
});

instance.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response;
  },
  async function (e) {
    const statusCode = e?.response?.status || 0;

    if (e instanceof AxiosError) {
      const { isLoggedIn, logout } = useAuthStore();

      if ([401].includes(statusCode) && isLoggedIn) {
        // auto logout if 401 Unauthorized response returned from api
        await logout();
        throw buildUnexpectedError("Session expired", e);
      }
      // handle this in LoginView.vue
      if (
        [401].includes(statusCode) &&
        !isLoggedIn &&
        e?.response?.data?.detail === "Password change required"
      ) {
        // pass this through as is
        throw buildUnexpectedError("Password change required", e);
      }
      if ([401].includes(statusCode) && !isLoggedIn) {
        throw buildUnexpectedError("Unauthorized", e);
      }
      if ([409].includes(statusCode)) {
        const conflictData = e?.response?.data;
        return Promise.reject({
          success: false,
          status: 409,
          data: conflictData,
        });
      }
      if ([429].includes(statusCode)) {
        const conflictData = e?.response?.data;
        return Promise.reject({
          success: false,
          status: 429,
          data: conflictData,
        });
      }

      if ([404].includes(statusCode)) {
        const error = e?.response?.data?.error;

        return Promise.reject({
          success: 404,
          status: 404,
          data: error,
        });
      }
    }

    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error

    const responseData = e?.response?.data;
    const combinedErrorMessage = extractCombinedErrorMessage(responseData);

    if (combinedErrorMessage && statusCode >= 500 && statusCode <= 599) {
      throw buildUnexpectedError(
        combinedErrorMessage || "Internal Server Error",
        e
      );
    } else if (combinedErrorMessage && statusCode != 401) {
      throw buildUnexpectedError(combinedErrorMessage, e);
    }

    throw e;
  }
);

export default instance;

export type AxiosInstance = ReturnType<typeof instance>;
